import classNames from 'classnames';
import { useEffect, useId, useRef, useState } from 'react';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import Calendar from '@/components/organisms/calendar/calendar';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './low-price-calendar.module.scss';

// How many animation frames to wait for the dates to render
const FOCUS_ATTEMPTS = 20;

// Days of the neighbouring months are in the grid too, but hidden
const SELECTABLE_DAY =
  ':not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth)';

export interface LowPriceCalendarProps {
  /** The class name to apply */
  className?: string;
  /** A list of available dates that are highlighted in the calendar */
  highlightedDates?: Date[];
  /** Indicates whether the dropdown is in a loading state */
  isLoading?: boolean;
  /** Locale for the calendar labels */
  locale?: Locale;
  /** A list of low price dates to highlight in the calendar */
  lowPriceDates?: Date[];
  /** A list of offers to show in the calendar */
  offers?: Date[];
  /** An event that is triggered on change */
  onChange?: (selectedDates: Date[]) => void;
  /** Preselected date in the calendar */
  selectedDate?: Date | null;
}

const LowPriceCalendar = ({
  className,
  onChange,
  ...rest
}: LowPriceCalendarProps) => {
  const { t } = useI18n();
  const [isVisible, setIsVisible] = useState(false);
  const isLaptop = useWindowSize('laptop');
  const calendarId = useId();
  const calendarRef = useRef<HTMLDivElement>(null);
  const shouldFocusCalendarRef = useRef(false);

  const handleClick = () => {
    shouldFocusCalendarRef.current = !isVisible;
    setIsVisible(previous => !previous);
  };

  // The calendar opens above its toggle, so focus moves into it, to the
  // selected date or else the first date that can be chosen
  useEffect(() => {
    if (!isVisible || !shouldFocusCalendarRef.current) return;

    let frame = 0;
    let attempts = 0;

    // The dates can render a moment after the calendar mounts
    const focusDate = () => {
      const calendar = calendarRef.current;
      const date =
        calendar?.querySelector<HTMLElement>(
          `.react-calendar__tile--active${SELECTABLE_DAY}`,
        ) ??
        calendar?.querySelector<HTMLElement>(
          `.react-calendar__month-view__days__day${SELECTABLE_DAY}`,
        );

      attempts += 1;

      // Only cleared once done, as effects can run twice in development
      if (date || attempts >= FOCUS_ATTEMPTS) {
        shouldFocusCalendarRef.current = false;
        date?.focus();

        return;
      }

      frame = requestAnimationFrame(focusDate);
    };

    focusDate();

    return () => cancelAnimationFrame(frame);
  }, [isVisible]);

  return (
    <div className={classNames(styles.component, className)}>
      {isVisible && (
        <div ref={calendarRef} className={styles.calendar} id={calendarId}>
          <Calendar
            {...rest}
            customRangeStyle
            displayedMonths={isLaptop ? 2 : 1}
            onChange={onChange}
          />
        </div>
      )}

      <AnchorLink
        aria-controls={isVisible ? calendarId : undefined}
        aria-expanded={isVisible}
        onClick={handleClick}
        text={
          isVisible
            ? t('items.search.lowPriceCalendar.hidePriceCalendar')
            : t('items.search.lowPriceCalendar.showPriceCalendar')
        }
      />
    </div>
  );
};

LowPriceCalendar.displayName = 'LowPriceCalendar';

export default LowPriceCalendar;
